import React, { useState, useCallback } from 'react';
import { Plus, X, ChevronUp, ChevronDown, Zap, ZapOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Zone presets for blade positioning
const ZONES = [
    { label: 'Left', short: 'Left', xOffset: -0.4 },
    { label: 'Center-Left', short: 'C-Left', xOffset: -0.15 },
    { label: 'Center', short: 'Center', xOffset: 0.1 },
    { label: 'Center-Right', short: 'C-Right', xOffset: 0.35 },
    { label: 'Right', short: 'Right', xOffset: 0.6 },
];

const getZoneFromOffset = (xOffset) => {
    let closest = ZONES[0];
    let minDist = Infinity;
    for (const z of ZONES) {
        const d = Math.abs(z.xOffset - (xOffset || 0));
        if (d < minDist) { minDist = d; closest = z; }
    }
    return closest;
};

const PORT_TYPES = [
    { value: 'LAN', label: 'LAN', color: '#10b981', bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-400', dot: 'bg-emerald-400' },
    { value: 'WAN', label: 'WAN', color: '#ef4444', bg: 'bg-red-500/20', border: 'border-red-500/40', text: 'text-red-400', dot: 'bg-red-400' },
    { value: 'POE', label: 'POE', color: '#f59e0b', bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-400', dot: 'bg-amber-400' },
    { value: 'SFP', label: 'SFP', color: '#3b82f6', bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-400', dot: 'bg-blue-400' },
    { value: 'Console', label: 'Console', color: '#64748b', bg: 'bg-slate-500/20', border: 'border-slate-500/40', text: 'text-slate-400', dot: 'bg-slate-400' },
    { value: 'Normal', label: 'Normal', color: '#a78bfa', bg: 'bg-violet-500/20', border: 'border-violet-500/40', text: 'text-violet-400', dot: 'bg-violet-400' },
];

const getPortType = (val) => PORT_TYPES.find(p => p.value === val) || PORT_TYPES[0];

const PortConfigurator = ({ portBanks, onChange, onAddBank, onRemoveBank }) => {
    const [selectedBankId, setSelectedBankId] = useState(null);
    const [selectedPorts, setSelectedPorts] = useState([]); // Array of port indices

    const updateBank = useCallback((id, field, value) => {
        onChange(portBanks.map(b => b.id === id ? { ...b, [field]: value } : b));
    }, [portBanks, onChange]);

    // Update multiple fields at once to avoid stale state
    const updateBankMulti = useCallback((id, updates) => {
        onChange(portBanks.map(b => b.id === id ? { ...b, ...updates } : b));
    }, [portBanks, onChange]);

    // Get the type of an individual port (checks overrides first, then falls back to bank default)
    const getIndividualPortType = (bank, portIndex) => {
        if (bank.portOverrides && bank.portOverrides[portIndex]) {
            return bank.portOverrides[portIndex].type;
        }
        return bank.type;
    };

    // Get individual port status
    const getIndividualPortStatus = (bank, portIndex) => {
        if (bank.portOverrides && bank.portOverrides[portIndex]) {
            return bank.portOverrides[portIndex].status || 'inactive';
        }
        return 'inactive';
    };

    // Get individual port label
    const getIndividualPortLabel = (bank, portIndex) => {
        if (bank.portOverrides && bank.portOverrides[portIndex]) {
            return bank.portOverrides[portIndex].label || '';
        }
        return '';
    };

    // Set type for selected ports
    const setSelectedPortsType = (bankId, type) => {
        const bank = portBanks.find(b => b.id === bankId);
        if (!bank) return;

        if (selectedPorts.length === 0) {
            // No ports selected — change the entire bank's default type
            updateBank(bankId, 'type', type);
            return;
        }

        // Update individual port overrides
        const overrides = { ...(bank.portOverrides || {}) };
        selectedPorts.forEach(idx => {
            overrides[idx] = { ...(overrides[idx] || {}), type };
        });

        onChange(portBanks.map(b => b.id === bankId ? { ...b, portOverrides: overrides } : b));
        setSelectedPorts([]);
    };

    // Set status for selected ports (active / inactive)
    const setSelectedPortsStatus = (bankId, status) => {
        const bank = portBanks.find(b => b.id === bankId);
        if (!bank) return;

        const overrides = { ...(bank.portOverrides || {}) };
        const portsToUpdate = selectedPorts.length > 0 ? selectedPorts : Array.from({ length: bank.count }, (_, i) => i);
        portsToUpdate.forEach(idx => {
            overrides[idx] = { ...(overrides[idx] || { type: bank.type }), status };
        });

        onChange(portBanks.map(b => b.id === bankId ? { ...b, portOverrides: overrides } : b));
    };

    // Set label for a single selected port
    const setPortLabel = (bankId, portIdx, label) => {
        const bank = portBanks.find(b => b.id === bankId);
        if (!bank) return;

        const overrides = { ...(bank.portOverrides || {}) };
        overrides[portIdx] = { ...(overrides[portIdx] || { type: bank.type }), label };
        onChange(portBanks.map(b => b.id === bankId ? { ...b, portOverrides: overrides } : b));
    };

    // Toggle a single port's selection
    const togglePort = (idx, shiftKey) => {
        if (shiftKey && selectedPorts.length > 0) {
            // Shift-click: select range from last selected to current
            const last = selectedPorts[selectedPorts.length - 1];
            const start = Math.min(last, idx);
            const end = Math.max(last, idx);
            const range = [];
            for (let i = start; i <= end; i++) range.push(i);
            setSelectedPorts(prev => [...new Set([...prev, ...range])]);
        } else {
            setSelectedPorts(prev =>
                prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
            );
        }
    };

    // Select all ports in the bank
    const selectAll = (bank) => {
        setSelectedPorts(Array.from({ length: bank.count }, (_, i) => i));
    };

    // Clear selection
    const clearSelection = () => setSelectedPorts([]);

    // Handle bank selection change
    const handleBankSelect = (bankId) => {
        if (selectedBankId === bankId) {
            setSelectedBankId(null);
        } else {
            setSelectedBankId(bankId);
        }
        setSelectedPorts([]);
    };

    return (
        <div className="space-y-3">
            {/* ——— Visual Blade Face Preview ——— */}
            <div className="relative w-full rounded-xl overflow-hidden border border-white/[0.08]"
                style={{ aspectRatio: '19 / 3', background: 'linear-gradient(180deg, #1e1e1e 0%, #141414 50%, #111 100%)' }}
            >
                {/* Rack screw holes */}
                <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-white/10 bg-white/[0.03]" />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-white/10 bg-white/[0.03]" />

                {/* Brushed metal texture */}
                <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(255,255,255,0.03) 1px, rgba(255,255,255,0.03) 2px)' }} />

                {/* Zone divider lines */}
                <div className="absolute inset-0 flex pointer-events-none">
                    {ZONES.map((z, i) => (
                        <div key={z.label} className="flex-1 border-r border-dashed border-white/[0.04] last:border-r-0 flex items-end justify-center pb-0.5">
                            <span className="text-[6px] text-white/10 uppercase tracking-wider font-mono">{z.short}</span>
                        </div>
                    ))}
                </div>

                {/* Port groups positioned by zone — collision-aware layout */}
                {(() => {
                    // Group banks by zone index
                    const zoneGroups = {};
                    portBanks.forEach(bank => {
                        const zone = getZoneFromOffset(bank.xOffset);
                        const zoneIndex = ZONES.indexOf(zone);
                        if (!zoneGroups[zoneIndex]) zoneGroups[zoneIndex] = [];
                        zoneGroups[zoneIndex].push(bank);
                    });

                    // Render a flex container per occupied zone, handling wrapping naturally
                    return Object.entries(zoneGroups).map(([zoneIdx, banks]) => {
                        const zi = parseInt(zoneIdx);
                        const zoneCenterPercent = (zi * 20) + 10; // center of zone

                        return (
                            <div
                                key={`zone-${zi}`}
                                className="absolute top-1/2 flex items-center justify-center gap-[6px] transition-all mix-blend-screen"
                                style={{
                                    left: `${zoneCenterPercent}%`,
                                    transform: 'translate(-50%, -50%)',
                                    width: '18%', // Constrain to zone width
                                    flexWrap: 'wrap' // Allow wrapping if too many ports
                                }}
                            >
                                {banks.map((bank) => {
                                    const cols = bank.columns || Math.min(bank.count, 12);
                                    const isActive = selectedBankId === bank.id;

                                    return (
                                        <div
                                            key={bank.id}
                                            onClick={(e) => { e.stopPropagation(); handleBankSelect(bank.id); }}
                                            className={`rounded-sm cursor-pointer transition-all duration-300 border ${isActive ? 'border-white/30 bg-white/[0.08] shadow-lg shadow-white/5' : 'border-transparent hover:border-white/10 bg-white/[0.02]'}`}
                                        >
                                            <div
                                                className="grid gap-[2px] p-1"
                                                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
                                            >
                                                {Array.from({ length: Math.min(bank.count, 96) }).map((_, i) => {
                                                    const portType = getIndividualPortType(bank, i);
                                                    const pt = getPortType(portType);
                                                    return (
                                                        <div
                                                            key={i}
                                                            className="rounded-[1px]"
                                                            style={{
                                                                width: bank.type === 'SFP' ? '3px' : '5px',
                                                                height: bank.type === 'SFP' ? '6px' : '4px',
                                                                backgroundColor: pt.color,
                                                                opacity: isActive ? 0.9 : 0.4,
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    });
                })()}

                {/* Empty state */}
                {portBanks.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[10px] text-white/20 uppercase tracking-widest">No ports configured</span>
                    </div>
                )}
            </div>

            {/* ——— Port Group Cards ——— */}
            <div className="space-y-2">
                <AnimatePresence>
                    {portBanks.map((bank) => {
                        const pt = getPortType(bank.type);
                        const isSelected = selectedBankId === bank.id;
                        const cols = bank.columns || Math.min(bank.count, 12);

                        return (
                            <motion.div
                                key={bank.id}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                layout
                                className={`rounded-xl border transition-all ${isSelected
                                    ? `${pt.bg} ${pt.border} shadow-lg`
                                    : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'}`}
                            >
                                {/* Compact header row */}
                                <div
                                    className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
                                    onClick={() => handleBankSelect(bank.id)}
                                >
                                    <div className={`w-2 h-2 rounded-full ${pt.dot}`} />
                                    <span className={`text-xs font-semibold ${isSelected ? pt.text : 'text-white/60'}`}>
                                        {bank.type}
                                    </span>
                                    <span className="text-[10px] text-white/25 font-mono">×{bank.count}</span>
                                    <span className="text-[9px] text-white/15 ml-auto uppercase tracking-wider">{getZoneFromOffset(bank.xOffset).short} • {cols}col</span>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRemoveBank(bank.id); if (selectedBankId === bank.id) setSelectedBankId(null); }}
                                        className="p-1.5 -mr-1 text-white/15 hover:text-red-400 transition-colors"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>

                                {/* Expanded config (when selected) */}
                                <AnimatePresence>
                                    {isSelected && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-visible"
                                        >
                                            <div className="px-3 pb-3 space-y-3 border-t border-white/[0.06] pt-3">

                                                {/* Port Type Pill Bar */}
                                                <div>
                                                    <span className="text-[9px] text-white/25 uppercase tracking-widest block mb-1.5">
                                                        {selectedPorts.length > 0 ? `Type (${selectedPorts.length} ports selected)` : 'Default Type'}
                                                    </span>
                                                    <div className="flex gap-1 flex-wrap">
                                                        {PORT_TYPES.map(p => (
                                                            <button
                                                                type="button"
                                                                key={p.value}
                                                                onClick={(e) => { e.stopPropagation(); setSelectedPortsType(bank.id, p.value); }}
                                                                className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border transition-all ${bank.type === p.value && selectedPorts.length === 0
                                                                    ? `${p.bg} ${p.border} ${p.text}`
                                                                    : 'bg-white/[0.03] border-white/[0.06] text-white/30 hover:text-white/50'}`}
                                                            >
                                                                {p.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Port Status Toggle */}
                                                <div>
                                                    <span className="text-[9px] text-white/25 uppercase tracking-widest block mb-1.5">
                                                        {selectedPorts.length > 0 ? `Status (${selectedPorts.length} ports)` : 'Bulk Status'}
                                                    </span>
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); setSelectedPortsStatus(bank.id, 'active'); }}
                                                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20"
                                                        >
                                                            <Zap size={10} /> Set Active
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); setSelectedPortsStatus(bank.id, 'inactive'); }}
                                                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all bg-white/[0.03] border-white/[0.08] text-white/30 hover:text-white/50"
                                                        >
                                                            <ZapOff size={10} /> Set Inactive
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Port Count */}
                                                <div>
                                                    <span className="text-[9px] text-white/25 uppercase tracking-widest block mb-1.5">Port Count</span>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); updateBank(bank.id, 'count', Math.max(1, bank.count - 1)); setSelectedPorts([]); }}
                                                            className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/40 hover:text-white/70 flex items-center justify-center transition-colors"
                                                        >
                                                            <ChevronDown size={14} />
                                                        </button>
                                                        <input
                                                            type="number"
                                                            min="1" max="96"
                                                            value={bank.count}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => { updateBank(bank.id, 'count', Math.max(1, Math.min(96, parseInt(e.target.value) || 1))); setSelectedPorts([]); }}
                                                            className="w-14 text-center bg-black/30 border border-white/[0.08] rounded-lg text-white/80 text-sm py-1 font-mono focus:outline-none focus:border-white/20"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); updateBank(bank.id, 'count', Math.min(96, bank.count + 1)); setSelectedPorts([]); }}
                                                            className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/40 hover:text-white/70 flex items-center justify-center transition-colors"
                                                        >
                                                            <ChevronUp size={14} />
                                                        </button>
                                                        <div className="flex gap-1 ml-2 flex-wrap">
                                                            {[1, 2, 4, 8, 12, 24, 48].map(n => (
                                                                <button
                                                                    type="button"
                                                                    key={n}
                                                                    onClick={(e) => { e.stopPropagation(); updateBankMulti(bank.id, { count: n, columns: Math.min(n, 24) }); setSelectedPorts([]); }}
                                                                    className={`px-1.5 py-0.5 text-[9px] font-mono rounded border transition-all ${bank.count === n
                                                                        ? `${pt.bg} ${pt.border} ${pt.text}`
                                                                        : 'bg-white/[0.02] border-white/[0.05] text-white/25 hover:text-white/40'}`}
                                                                >
                                                                    {n}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Column Layout */}
                                                <div>
                                                    <span className="text-[9px] text-white/25 uppercase tracking-widest block mb-1.5">Column Layout</span>
                                                    <div className="flex gap-1">
                                                        {[1, 2, 4, 6, 8, 12, 16, 24].filter(c => c <= bank.count).map(c => (
                                                            <button
                                                                type="button"
                                                                key={c}
                                                                onClick={(e) => { e.stopPropagation(); updateBank(bank.id, 'columns', c); }}
                                                                className={`px-2 py-1 rounded text-[9px] font-mono border transition-all ${(bank.columns || 8) === c
                                                                    ? `${pt.bg} ${pt.border} ${pt.text}`
                                                                    : 'bg-white/[0.02] border-white/[0.05] text-white/25 hover:text-white/40'}`}
                                                            >
                                                                {c}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Position on Blade */}
                                                <div>
                                                    <span className="text-[9px] text-white/25 uppercase tracking-widest block mb-1.5">Position on Device</span>
                                                    <div className="flex gap-1">
                                                        {ZONES.map(z => {
                                                            const currentZone = getZoneFromOffset(bank.xOffset);
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    key={z.label}
                                                                    onClick={(e) => { e.stopPropagation(); onChange(portBanks.map(b => b.id === bank.id ? { ...b, xOffset: z.xOffset } : b)); }}
                                                                    className={`flex-1 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-wider border transition-all ${currentZone.label === z.label
                                                                        ? `${pt.bg} ${pt.border} ${pt.text}`
                                                                        : 'bg-white/[0.02] border-white/[0.05] text-white/20 hover:text-white/40 hover:bg-white/[0.04]'}`}
                                                                >
                                                                    {z.short}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* ——— Interactive Port Grid ——— */}
                                                <div>
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <span className="text-[9px] text-white/25 uppercase tracking-widest">
                                                            Port Map
                                                            {selectedPorts.length > 0 && (
                                                                <span className="text-white/40 ml-1">({selectedPorts.length} selected)</span>
                                                            )}
                                                        </span>
                                                        <div className="flex gap-2">
                                                            {selectedPorts.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); clearSelection(); }}
                                                                    className="text-[8px] text-white/30 hover:text-white/50 uppercase tracking-wider transition-colors"
                                                                >
                                                                    Clear
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); selectAll(bank); }}
                                                                className="text-[8px] text-white/30 hover:text-white/50 uppercase tracking-wider transition-colors"
                                                            >
                                                                All
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className="bg-black/30 rounded-lg border border-white/[0.06] p-2"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div
                                                            className="grid gap-[3px]"
                                                            style={{ gridTemplateColumns: `repeat(${bank.columns || cols}, minmax(0, 1fr))` }}
                                                        >
                                                            {Array.from({ length: bank.count }).map((_, i) => {
                                                                const portType = getIndividualPortType(bank, i);
                                                                const portPt = getPortType(portType);
                                                                const portStatus = getIndividualPortStatus(bank, i);
                                                                const portLabel = getIndividualPortLabel(bank, i);
                                                                const isPortSelected = selectedPorts.includes(i);
                                                                const hasOverride = bank.portOverrides && bank.portOverrides[i];
                                                                const isActive = portStatus === 'active';

                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        key={i}
                                                                        onClick={(e) => { e.stopPropagation(); togglePort(i, e.shiftKey); }}
                                                                        className={`relative flex items-center justify-center rounded-sm transition-all text-[7px] font-mono
                                                                            ${isPortSelected
                                                                                ? 'ring-1 ring-white/60 z-10 scale-110'
                                                                                : 'hover:brightness-150 hover:scale-105'
                                                                            }`}
                                                                        style={{
                                                                            backgroundColor: isActive ? portPt.color : '#1e293b',
                                                                            opacity: isPortSelected ? 1 : (isActive ? 0.85 : 0.4),
                                                                            aspectRatio: bank.type === 'SFP' ? '1 / 2' : '3 / 2',
                                                                            minHeight: '14px',
                                                                        }}
                                                                        title={`Port ${i + 1} — ${portType} — ${portStatus}${portLabel ? ` — ${portLabel}` : ''}${hasOverride ? ' (custom)' : ''}`}
                                                                    >
                                                                        <span className="text-black/50 font-bold leading-none" style={{ fontSize: '6px' }}>
                                                                            {i + 1}
                                                                        </span>
                                                                        {/* Active indicator dot */}
                                                                        {isActive && !isPortSelected && (
                                                                            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 border border-black/30 shadow-[0_0_3px_rgba(16,185,129,0.6)]" />
                                                                        )}
                                                                        {/* Override (non-active) indicator */}
                                                                        {hasOverride && !isActive && !isPortSelected && (
                                                                            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-white/40 border border-black/30" />
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Port Label Editor — shows when exactly 1 port is selected */}
                                                        {selectedPorts.length === 1 && (() => {
                                                            const pIdx = selectedPorts[0];
                                                            const currentLabel = getIndividualPortLabel(bank, pIdx);
                                                            const currentStatus = getIndividualPortStatus(bank, pIdx);
                                                            return (
                                                                <div className="mt-2 bg-white/[0.03] border border-white/[0.08] rounded-lg p-2 space-y-1.5">
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-[9px] text-white/40 font-semibold uppercase tracking-wider">Port {pIdx + 1} Details</span>
                                                                        <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${currentStatus === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-white/30'}`}>
                                                                            {currentStatus}
                                                                        </span>
                                                                    </div>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Label (e.g. POS-1, AP-Kitchen)"
                                                                        value={currentLabel}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onChange={(e) => { e.stopPropagation(); setPortLabel(bank.id, pIdx, e.target.value); }}
                                                                        className="w-full bg-black/30 border border-white/[0.08] rounded text-[10px] text-white/70 px-2 py-1.5 focus:outline-none focus:border-white/20 placeholder:text-white/15 font-mono"
                                                                    />
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Selection hint */}
                                                        <div className="mt-2 text-[8px] text-white/20 text-center font-mono">
                                                            {selectedPorts.length > 1
                                                                ? 'Choose a type or status above to apply to selected ports'
                                                                : selectedPorts.length === 1
                                                                    ? 'Edit port label above • Choose type/status to change'
                                                                    : 'Click ports to select • Shift+click for range'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>

            {/* Add Port Group Button */}
            <button
                type="button"
                onClick={onAddBank}
                className="w-full py-2.5 rounded-xl border border-dashed border-white/[0.08] hover:border-white/[0.15] bg-white/[0.01] hover:bg-white/[0.03] text-white/30 hover:text-white/50 text-[10px] uppercase tracking-widest font-semibold flex items-center justify-center gap-1.5 transition-all"
            >
                <Plus size={12} />
                Add Port Group
            </button>
        </div>
    );
};

export default PortConfigurator;
